import { Controller } from "@hotwired/stimulus"

// Shared "copy to clipboard" controller.
//
// Accepts three view dialects:
//   1. `data-clipboard-url-value="..."` — explicit URL/value to copy
//   2. `data-clipboard-target="source"` — copies an input/textarea's value (falls back to its textContent)
//   3. `data-clipboard-target="label"`  — swaps the label text to "Copied!"
//
// Visual feedback adapts to whatever targets the view provides (each optional):
//   label text swap, copyIcon/checkIcon swap, copyText/copiedText swap,
//   and as a last resort a brief green border flash + title on the `button` target.
export default class extends Controller {
  static targets = ["label", "source", "button", "copyIcon", "checkIcon", "copyText", "copiedText"]
  static values = {
    url: String
  }

  async copy() {
    const text = this.textToCopy
    if (!text) {
      console.error("[clipboard] Nothing to copy: no url value and no source target on", this.element)
      return
    }

    let copied = false
    try {
      await navigator.clipboard.writeText(text)
      copied = true
    } catch (err) {
      console.error("[clipboard] Failed to copy:", err)
      copied = this.copyViaExecCommand(text)
    }

    if (copied) this.showCopied()
  }

  get textToCopy() {
    if (this.hasUrlValue && this.urlValue.length > 0) return this.urlValue
    if (this.hasSourceTarget) {
      const source = this.sourceTarget
      return source.value || source.textContent?.trim() || ""
    }
    return ""
  }

  copyViaExecCommand(text) {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.select()
    try {
      return document.execCommand("copy")
    } catch (e) {
      console.error("[clipboard] Fallback copy failed:", e)
      return false
    } finally {
      document.body.removeChild(textarea)
    }
  }

  showCopied() {
    if (this.resetTimer) clearTimeout(this.resetTimer)
    this.resetFeedback()

    if (this.hasLabelTarget) {
      this.originalLabels = this.labelTargets.map((el) => el.textContent)
      this.labelTargets.forEach((el) => (el.textContent = "Copied!"))
    }

    if (this.hasCopyIconTarget || this.hasCheckIconTarget) {
      this.copyIconTargets.forEach((el) => el.classList.add("hidden"))
      this.checkIconTargets.forEach((el) => el.classList.remove("hidden"))
    }

    if (this.hasCopyTextTarget || this.hasCopiedTextTarget) {
      this.copyTextTargets.forEach((el) => el.classList.add("hidden"))
      this.copiedTextTargets.forEach((el) => el.classList.remove("hidden"))
    }

    if (this.hasButtonTarget && !this.hasLabelTarget && !this.hasCopyIconTarget && !this.hasCopyTextTarget) {
      this.originalTitle = this.buttonTarget.getAttribute("title")
      this.originalBorderColor = this.buttonTarget.style.borderColor ?? ""
      this.buttonTarget.setAttribute("title", "Copied!")
      this.buttonTarget.style.borderColor = "#12B76A"
      this.buttonFlashed = true
    }

    this.resetTimer = setTimeout(() => this.resetFeedback(), 2000)
  }

  resetFeedback() {
    if (this.originalLabels) {
      this.labelTargets.forEach((el, i) => (el.textContent = this.originalLabels[i]))
      this.originalLabels = null
    }

    this.copyIconTargets.forEach((el) => el.classList.remove("hidden"))
    this.checkIconTargets.forEach((el) => el.classList.add("hidden"))
    this.copyTextTargets.forEach((el) => el.classList.remove("hidden"))
    this.copiedTextTargets.forEach((el) => el.classList.add("hidden"))

    if (this.hasButtonTarget && this.buttonFlashed) {
      this.buttonTarget.style.borderColor = this.originalBorderColor
      if (this.originalTitle) this.buttonTarget.setAttribute("title", this.originalTitle)
      this.buttonFlashed = false
      this.originalBorderColor = null
      this.originalTitle = null
    }
  }

  disconnect() {
    if (this.resetTimer) clearTimeout(this.resetTimer)
  }
}
